/**
 * アイドルタイムアウト検知フック
 *
 * ユーザーの操作（マウス移動、キー入力、クリック等）を監視し、
 * 設定時間無操作で警告→自動ログアウトする。
 * localStorage を使用して複数タブ間で最終操作時刻を共有する。
 */
import { useEffect, useRef, useCallback } from 'react';

const STORAGE_KEY = 'acecollect_last_activity';
const CHECK_INTERVAL_MS = 60_000; // 1分ごとにチェック
const ACTIVITY_THROTTLE_MS = 30_000; // 操作記録のスロットル（30秒）
const WARNING_BEFORE_MINUTES = 5; // タイムアウト5分前に警告

// 監視するユーザー操作イベント
const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousemove',
  'keydown',
  'click',
  'scroll',
  'touchstart',
];

interface UseIdleTimeoutOptions {
  /** アイドルタイムアウト（分）。0以下で無効 */
  timeoutMinutes: number;
  /** 警告コールバック（タイムアウトN分前に呼ばれる） */
  onWarning: () => void;
  /** タイムアウト到達コールバック */
  onTimeout: () => void;
  /** 有効フラグ（未ログインでは無効） */
  enabled: boolean;
}

/** 最終操作時刻を localStorage に記録する */
function recordActivity(): void {
  localStorage.setItem(STORAGE_KEY, Date.now().toString());
}

/** 最終操作時刻を localStorage から取得する */
function getLastActivity(): number {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? parseInt(stored, 10) : Date.now();
}

/**
 * アイドルタイムアウト検知フック
 *
 * @example
 * useIdleTimeout({
 *   timeoutMinutes: 60,
 *   onWarning: () => setShowWarning(true),
 *   onTimeout: () => logout(),
 *   enabled: !!user,
 * });
 */
export function useIdleTimeout({
  timeoutMinutes,
  onWarning,
  onTimeout,
  enabled,
}: UseIdleTimeoutOptions): {
  /** タイマーをリセットする（警告モーダルの「継続」ボタン用） */
  resetTimer: () => void;
} {
  const lastThrottleRef = useRef(0);
  const warningFiredRef = useRef(false);
  const onWarningRef = useRef(onWarning);
  const onTimeoutRef = useRef(onTimeout);

  // コールバックの最新参照を保持
  useEffect(() => {
    onWarningRef.current = onWarning;
    onTimeoutRef.current = onTimeout;
  }, [onWarning, onTimeout]);

  /** タイマーリセット（外部から呼び出し可能） */
  const resetTimer = useCallback(() => {
    recordActivity();
    warningFiredRef.current = false;
  }, []);

  // ユーザー操作イベントのリスナー設定
  useEffect(() => {
    if (!enabled || timeoutMinutes <= 0) return;

    const handleActivity = () => {
      const now = Date.now();
      // スロットル: 30秒以内の連続記録を抑制
      if (now - lastThrottleRef.current < ACTIVITY_THROTTLE_MS) return;
      lastThrottleRef.current = now;
      recordActivity();
      warningFiredRef.current = false;
    };

    // 初回: 現在時刻を記録
    recordActivity();

    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, handleActivity, { passive: true });
    }

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, handleActivity);
      }
    };
  }, [enabled, timeoutMinutes]);

  // タイムアウトチェック用インターバル
  useEffect(() => {
    if (!enabled || timeoutMinutes <= 0) return;

    const timeoutMs = timeoutMinutes * 60_000;
    const warningMs = Math.max(0, timeoutMs - WARNING_BEFORE_MINUTES * 60_000);

    const intervalId = setInterval(() => {
      const lastActivity = getLastActivity();
      const elapsed = Date.now() - lastActivity;

      // タイムアウト到達
      if (elapsed >= timeoutMs) {
        onTimeoutRef.current();
        return;
      }

      // 警告（タイムアウト5分前、1回のみ）
      if (elapsed >= warningMs && !warningFiredRef.current) {
        warningFiredRef.current = true;
        onWarningRef.current();
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [enabled, timeoutMinutes]);

  // 別タブでの操作を検知（storage イベント）
  useEffect(() => {
    if (!enabled) return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        // 別タブで操作があったので警告をリセット
        warningFiredRef.current = false;
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [enabled]);

  return { resetTimer };
}
